import type { ComponentType, ReactNode } from "react";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { EditarItemDialog } from "@/components/catalogo/EditarItemDialog";
import { EliminarItemDialog } from "@/components/catalogo/EliminarItemDialog";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { useCatalogo, useCrearItem } from "@/hooks/useCatalogo";
import { getApiErrorMessage } from "@/lib/api";
import {
  errorDeNombreDeItem,
  type ItemDeCatalogo,
  type RecursoDeCatalogo,
} from "@/lib/catalogos";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";

const FILAS_SKELETON = 4;

const altaSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio."),
});

type AltaValues = z.infer<typeof altaSchema>;

export interface MantenedorDeCatalogoProps {
  /** Segmento de la API (`/api/<recurso>`). */
  recurso: RecursoDeCatalogo;
  /** Rótulo en singular, en minúscula ("contexto", "ítem de revisión"). */
  singular: string;
  /** Rótulo en plural, en minúscula ("contextos", "ítems de revisión"). */
  plural: string;
  /** Bajada de la pantalla: qué es este catálogo y para qué sirve. */
  descripcion: ReactNode;
  /** Letra chica: las reglas que el usuario necesita saber antes de tocar. */
  ayuda: ReactNode;
  /** Ejemplo real para el placeholder del alta. */
  ejemplo: string;
  /** Ícono del estado vacío. */
  icono: ComponentType<{ className?: string }>;
  /** Copy del estado vacío. */
  vacio: { titulo: string; descripcion: string };
}

/**
 * Mantenedor genérico de un catálogo ORDENADO (`id, nombre, orden`).
 *
 * Tres pantallas del Super Admin comparten esta forma exacta; este componente
 * es esa forma, una sola vez. El catálogo es una SECUENCIA (el orden importa),
 * así que se dibuja como lista numerada y no como tabla de filas equivalentes:
 * el número de posición es parte del dato, no decoración. El alta va arriba,
 * en la misma pantalla, porque es un solo campo y el resultado se ve abajo al
 * instante.
 *
 * NO lo usa `Contenidos` todavía: su borrado tiene una guarda de referencia
 * (422 + `informes_count` + fila "Bloqueado por el sistema") que estos
 * catálogos no tienen, porque las tablas que los van a referenciar son de
 * fases posteriores. Agregar esa rama acá sería código muerto para los dos
 * consumidores actuales; cuando `Contenidos` migre, se agrega entonces.
 *
 * El título y los breadcrumbs NO se renderizan acá: viven en el `TopBar` y se
 * declaran en `lib/pageMeta.ts` (convención del Design System).
 */
export function MantenedorDeCatalogo({
  recurso,
  singular,
  plural,
  descripcion,
  ayuda,
  ejemplo,
  icono: Icono,
  vacio,
}: MantenedorDeCatalogoProps) {
  const catalogo = useCatalogo(recurso);
  const crear = useCrearItem(recurso);
  const [aEditar, setAEditar] = useState<ItemDeCatalogo | null>(null);
  const [aEliminar, setAEliminar] = useState<ItemDeCatalogo | null>(null);

  const form = useForm<AltaValues>({
    resolver: zodResolver(altaSchema),
    defaultValues: { nombre: "" },
  });

  const total = catalogo.data?.length ?? 0;
  // Los cuatro estados de la consulta (ver `lib/estadoDeConsulta.ts`).
  const estado = estadoDeConsulta(catalogo);

  function handleCrear(valores: AltaValues) {
    crear.mutate(
      { nombre: valores.nombre },
      {
        onSuccess: (item) => {
          toast.success(`«${item.nombre}» agregado al catálogo`);
          form.reset({ nombre: "" });
        },
        onError: (error) => {
          const mensaje = errorDeNombreDeItem(error);
          if (mensaje) {
            form.setError("nombre", { type: "server", message: mensaje });
            return;
          }
          toast.error(
            getApiErrorMessage(error, `No pudimos crear el ${singular}. Inténtalo de nuevo.`),
          );
        },
      },
    );
  }

  return (
    <div className="space-y-6 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div>
        <p className="text-muted-foreground text-caption">{descripcion}</p>
        <p className="text-ink-hint mt-0.5 max-w-[70ch] text-xs">{ayuda}</p>
      </div>

      {/* Alta: un solo campo, en la misma pantalla que el listado. */}
      <div className="bg-card w-full max-w-[35rem] rounded-xl border p-6">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleCrear)}
            className="flex flex-wrap items-start gap-3"
          >
            <FormField
              control={form.control}
              name="nombre"
              render={({ field }) => (
                <FormItem className="min-w-[13.75rem] flex-1">
                  <FormLabel>Nombre del {singular}</FormLabel>
                  <FormControl>
                    <Input placeholder={`Ej: ${ejemplo}`} autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={crear.isPending} className="mt-[1.625rem] gap-2">
              {crear.isPending && <Spinner className="text-primary-foreground" />}
              <Plus className="h-4 w-4" />
              Crear {singular}
            </Button>
          </form>
        </Form>
      </div>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar el catálogo"
          error={catalogo.error}
          onReintentar={() => catalogo.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que={`el catálogo de ${plural}`} />
      ) : estado.cargando ? (
        <div className="w-full max-w-[35rem] space-y-3">
          <Skeleton className="h-4 w-40" />
          <div className="bg-card divide-y overflow-hidden rounded-xl border">
            {Array.from({ length: FILAS_SKELETON }).map((_, i) => (
              // El `role`/`aria-label` es lo que deja que un test afirme que la
              // carga se dibuja (y que borrar esta rama ponga la suite en rojo).
              <div
                key={i}
                role="status"
                aria-label="Cargando catálogo"
                className="flex items-center gap-3 px-4 py-3.5"
              >
                <Skeleton className="size-7 rounded-full" />
                <Skeleton className="h-3.5 w-48" />
              </div>
            ))}
          </div>
        </div>
      ) : total === 0 ? (
        <Empty className="w-full max-w-[35rem] rounded-xl border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Icono />
            </EmptyMedia>
            <EmptyTitle>{vacio.titulo}</EmptyTitle>
            <EmptyDescription>{vacio.descripcion}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="w-full max-w-[35rem] space-y-3">
          <p className="text-sm font-bold">
            {total} {total === 1 ? singular : plural}
          </p>
          <ol className="bg-card divide-y overflow-hidden rounded-xl border">
            {catalogo.data?.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="bg-primary/10 text-primary flex size-7 shrink-0 items-center justify-center rounded-full font-mono text-xs font-bold">
                  {item.orden}
                </span>
                <span className="flex-1 text-sm font-bold">{item.nombre}</span>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setAEditar(item)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setAEliminar(item)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Eliminar
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Los `key` REMONTAN los diálogos al cambiar de ítem: sin ellos, el
          formulario conservaría el nombre del anterior (react-hook-form solo
          lee `defaultValues` al montar) y el error del intento anterior
          seguiría en pantalla sobre un ítem que nunca se intentó borrar. */}
      <EditarItemDialog
        key={`editar-${aEditar?.id}`}
        recurso={recurso}
        item={aEditar}
        singular={singular}
        onCerrar={() => setAEditar(null)}
      />
      <EliminarItemDialog
        key={`eliminar-${aEliminar?.id}`}
        recurso={recurso}
        item={aEliminar}
        singular={singular}
        onCerrar={() => setAEliminar(null)}
      />
    </div>
  );
}
